import StepRoutes from "../constants/Routes";
import { VisibleLayoutEnum } from "../features/FormEnrollmentNew/FormEnrollmentNew";
import { useStore } from "../store/store";
import { AccordionRoutesEnum } from "../types/AccordionRoutes";
import { SituationsStep } from "../types/ConfigModels";
import { DynamicsApiEnrollRequestConsents, PayloadModel } from "../types/PayloadModel";
import { TrackingFlowTypeEnum } from "../types/TrackingFlowTypeEnum";
import { TranslationModel } from "../types/TranslationModel";
import { TranslationModelOld } from "../types/TranslationModelOld";
import { getCookie, setCookie } from "./cookieService";
import md5 from "md5";

declare global {
  interface Window {
    dataLayer: any;
  }
}

window.dataLayer = window.dataLayer || [];

// Reads the current value directly from the store; must not be called as a hook outside a component.
const getFromSalaryInsuranceConfig = () => useStore.getState().fromSalaryInsuranceConfigCard;

// Normalise the email address
const transformEmailForEC = (email: any) => {
  email = email.trim().toLowerCase().split('@');

  return (email[1] === 'gmail.com' || email[1] === 'googlemail.com')
    ? email[0].replaceAll('.', '').split('+')[0] + '@' + email[1]
    : email[0] + '@' + email[1];
}

// SHA-256 hash function
const digestMessage = async (message: any) => {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
}

class Analytics {
  private static instance: Analytics;
  private products: any[] = [];

  constructor() {}

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }

    return Analytics.instance;
  }

  private clearEcommerce() {
    window.dataLayer.push({
      event: "event_null",
      ecommerce: null,
    });
  }

  public trackProducts(payload: PayloadModel, offerEnabled: boolean, translations: TranslationModel | TranslationModelOld | null, flow: TrackingFlowTypeEnum, salaryInsuranceCalculationPrice?: number) {
    if (window.dataLayer) {
      this.products = [];
      const discountEnabled = offerEnabled && payload.wantsUnemploymentBenefits && payload.wantsUnion && !payload.wantsSalaryInsuranceAndUnion;

      if (payload.wantsUnemploymentBenefits && flow !== TrackingFlowTypeEnum.SalaryConfig) {
        this.products.push({
          item_name: "a-kasse",
          item_id: "A",
          price: translations?.productsStep.unemploymentInsurance.price, 
          item_brand: "ase",
          item_category: "insurance",
          item_category2: "unemployment fund",
          item_variant: payload.occupation,
          quantity: 1,
        });
      }

      if (payload.wantsUnion) {
        let event = {
          item_name: payload.wantsUnion ? "fagforening" : "fagforening_familie",
          item_id: "AL",
          price: payload.wantsUnion && !discountEnabled ? translations?.productsStep.union.price
          : 0,
          item_brand: "ase lønmodtager",
          item_category: "consulting",
          item_category2: "union",
          item_variant: payload.wantsUnion ? "full" : "family",
          quantity: 1,
        } as any;

        if (discountEnabled) {
          event = {
            ...event,
            discount: translations?.productsStep.union.price,
            coupon: "rebate6mo",
          };
        }

        this.products.push(event);
      }

      if (payload.wantsSalaryInsurance && flow !== TrackingFlowTypeEnum.SalaryConfig) {
        this.products.push({
          item_name: "lønsikring",
          item_id: "L",
          price: 0,
          item_brand: "ase",
          item_category: "service",
          item_category2: "callback",
          quantity: 1,
          index: 1,
        });
      }

      if (payload.wantsHRJura) {
        this.products.push({
          item_name: "hr jura",
          item_id: "HR",
          price: translations?.productsStep.hrjura.price,
          item_brand: "ase selvstændig",
          item_category: "consulting",
          item_category2: "union",
          item_variant: "full",
          quantity: 1,
        });
      }

      if (payload.wantsBasic) {
        this.products.push({
          item_name: "ase selvstændig",
          item_id: "SB",
          price: payload.wantsUnemploymentBenefits ? translations?.productsStep.basic.price : "149",
          item_brand: "ase selvstændig",
          item_category: "consulting",
          item_category2: "union",
          item_variant: "family",
          quantity: 1,
        });
      }

      if (payload.wantsSalaryInsuranceAndUnion) {
        this.products.push({
          item_name: "fagforening+lønsikring-rmo",
          item_id: "s4",
          price: "699",
          item_brand: "ase",
          item_category: "service",
          item_category2: "",
          item_variant: "alm",
          quantity: 1,
        });
      }

      if (flow === TrackingFlowTypeEnum.SalaryConfig) {
        this.products.push({
        item_name: "lønsikring",
        item_id: "L",
        price: salaryInsuranceCalculationPrice,
        item_brand: "ase",
        item_category: "service",
        item_category2: "callback",
        quantity: 1,
        index: 1,
        });
        
        this.products.push({
        item_name: "a-kasse",
        item_id: "A",
        price: translations?.productsStep.unemploymentInsurance.price,
        item_brand: "ase",
        item_category: "insurance",
        item_category2: "unemployment fund",
        item_variant: payload.occupation,
        quantity: 1,
        });
      }

      this.products = this.products.map((p, index) => ({
        ...p,
        index: index + 1,
      }));

      const ecommerce = {
        currency: "DKK",
        items: this.products,
      };

      this.clearEcommerce();

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce,
        flow,
      });
    }
  }

  public trackUnion(type: "union", translations: TranslationModelOld | TranslationModel | null, flow: TrackingFlowTypeEnum) {
    if (window.dataLayer) {
      this.products = [];

      if (type === "union") {
        let event = {
          item_name: "fagforening",
          item_id: "AL",
          price: translations?.productsStep.union.price,
          item_brand: "ase lønmodtager",
          item_category: "consulting",
          item_category2: "union",
          item_variant: "full",
          quantity: 1,
          discount: translations?.productsStep.union.price,
          coupon: "rebate6mo",
        } as any;

        this.products.push(event);
      }

      this.products = this.products.map((p, index) => ({
        ...p,
        index: index + 1,
      }));

      const ecommerce = {
        currency: "DKK",
        items: this.products,
      };

      this.clearEcommerce();

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce,
        flow,
      });
    }
  }

  public trackTerminateUnemploymentBenefits(flow: TrackingFlowTypeEnum) {
    if (window.dataLayer) {
      const item = {
        item_name: "a-kasse_move",
        item_id: "s1",
        price: 0,
        item_brand: "ase",
        item_category: "service",
        item_category2: "terminate",
        item_variant: "full",
        quantity: 1,
        index: this.products.length,
      };

      this.products.push(item);

      this.clearEcommerce();

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "DKK",
          items: [item],
        },
        flow,
      });
    }
  }

  public trackTerminateUnion(flow: TrackingFlowTypeEnum) {
    if (window.dataLayer) {
      const item = {
        item_name: "union_terminate",
        item_id: "s2",
        price: 0,
        item_brand: "ase",
        item_category: "service",
        item_category2: "terminate",
        item_variant: "full",
        quantity: 1,
        index: this.products.length,
      };

      this.products.push(item);

      this.clearEcommerce();

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "DKK",
          items: [item],
        },
        flow,
      });
    }
  }

  public trackStep(stepName: string, flow: TrackingFlowTypeEnum, payload: PayloadModel) {
    let flowType = getFromSalaryInsuranceConfig() ? TrackingFlowTypeEnum.SalaryConfig : flow;

    if (window.dataLayer) {
      if(stepName === StepRoutes.CompletedStep || stepName === AccordionRoutesEnum.ContactMeReciept || stepName === AccordionRoutesEnum.ThankYouStep) {
        window.dataLayer.push({
          event: "checkout",
          content_name: stepName,
          flow: flowType,
        });
      }else if ( stepName === AccordionRoutesEnum.ProductStepV2){
          window.dataLayer.push({
          event: "checkout",
          content_name: stepName,
          version: "ds8362_control",
          flow: flowType,
        });
      } else if (stepName === AccordionRoutesEnum.ProductStep){
        window.dataLayer.push({
          event: "checkout",
          content_name: stepName,
          version: "ds8362_variant",
          flow: flowType,
        });
      } else {
        window.dataLayer.push({
          event: "checkout",
          content_name: stepName,
          flow: flowType,
        });
      }
    }
  }

  public trackStepCount = (count: number, flow: TrackingFlowTypeEnum, payload: PayloadModel) => {
    let flowType = getFromSalaryInsuranceConfig() ? TrackingFlowTypeEnum.SalaryConfig : flow;
    
    if (window.dataLayer) {
      digestMessage(transformEmailForEC(payload.mailAddress)).then((digestHex) => window.dataLayer.push({
        event: "checkout_completed",
        content_name: StepRoutes.PaymentStep,
        steps_completed: count,
        flow: flowType,
        sha256: digestHex,
      }));
    }
  };

  public trackNeedToContact = (payload: PayloadModel, flow: TrackingFlowTypeEnum, email: any) => {
    let flowType = getFromSalaryInsuranceConfig() ? TrackingFlowTypeEnum.SalaryConfig : flow;
    
    if (window.dataLayer) {
      digestMessage(transformEmailForEC(email)).then((digestHex) => window.dataLayer.push({
        event: "checkout_completed",
        content_name: "NeedToContactYouStepCompleted",
        cause: payload.cause,
        flow: flowType,
        sha256: digestHex,
      }))
    }
  };

  public trackPurchase(payload: PayloadModel, offerEnabled: boolean, totalPrice: number, flow: TrackingFlowTypeEnum) {
    let flowType = getFromSalaryInsuranceConfig() ? TrackingFlowTypeEnum.SalaryConfig : flow;

    if (window.dataLayer) {
      const ecommerce = {
        currency: "DKK",
        items: this.products,
        payment_type: `${payload.paymentType}_${payload.paymentFrequency}`,
        value: totalPrice,
        transaction_id: Date.now(),
      };

      if (payload.mailAddress) {
        (ecommerce as any).emd5 = md5(payload.mailAddress.toLowerCase().trim());
      }

      if (offerEnabled && payload.wantsUnemploymentBenefits && payload.wantsUnion && !payload.wantsSalaryInsuranceAndUnion) {
        (ecommerce as any).offerEnabled = "rebate6mo";
      }
      
      this.clearEcommerce();

      window.dataLayer.push({
        event: "purchase",
        ecommerce,
        flow: flowType,
      });
    }
  }

  public trackAllConsent(name: "added" | "collected", consents: DynamicsApiEnrollRequestConsents | undefined, flow: TrackingFlowTypeEnum) {
    let flowType = getFromSalaryInsuranceConfig() ? TrackingFlowTypeEnum.SalaryConfig : flow;

    if (window.dataLayer) {
      let params = "";

      if (consents?.unemploymentBenefits?.aseCooperation) {
        params += "aa_collaboration,";
      }

      if (consents?.unemploymentBenefits?.member) {
        params += "aa_news,";
      }

      if (consents?.unemploymentBenefits?.partner) {
        params += "aa_partner,";
      }

      if (consents?.union?.aseCooperation) {
        params += "af_collaboration,";
      }

      if (consents?.union?.member) {
        params += "af_news,";
      }

      if (consents?.union?.partner) {
        params += "af_partner,";
      }

      window.dataLayer.push({
        event: "permissions",
        content_name: name,
        content_type: params,
        flow: flowType,
      });
    }
  }

  public trackPaymentFrequency(frequency: "monthly" | "quarterly") {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "checkout",
        content_name: "payment_frequency",
        content_type: frequency,
      });
    }
  }

  public trackContactLeanModalClosed() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "content_select",
        "tga-event": true,
        content_name: "button",
        content_type: "enrollment_abandoned_closed",
      });
    }
  }

  public trackContactLeanModalSubmitted() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "enrollment_advice",
        "tga-event": true,
        leadkampagne: "enrollment_abandoned",
        leadtype: "callback",
      });
    }
  }

  public trackContactLeanModalPopped() {
    if (window.dataLayer) {
      const eventEmitted = getCookie("ContactLeanModalPoppedEvent");
      if (!eventEmitted) {
        window.dataLayer.push({
          event: "popup",
          "tga-event": true,
          content_name: "popup_shown",
          content_type: "enrollment_abandoned",
        });
        setCookie("ContactLeanModalPoppedEvent", "true", 8);
      } else {
        setCookie("ContactLeanModalPoppedEvent", "", -1);
      }
    }
  }

  public trackSubmitChildrenEnrollment(numberOfChildren: number) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "form_submit",
        "tga-event": true,
        content_type: "family_enrollment",
        content_name: `add_children_${numberOfChildren}`,
      });
    }
  }

  public trackSalaryInsuranceAction(type: "presented" | "updated" | "selected" | "form_completed" | "form_closed" | "go_to_checkout", salary: string, insuranceAmount: string, duration: string, price: string, fullAmount: string, name?: string) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_type: name ? name : "salary_insurance",
        content_name: type,
        salary: salary,
        length: duration,
        sum: insuranceAmount,
        price: price,
        full_amount: fullAmount
      });
    }
  }

  public trackCircumstanceStep = (circumstanceOptions: any, event_name: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: event_name,
        questions: circumstanceOptions
      });
    }
  }

  public trackChosenProducts(products: string) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "productcard",
        content_type: "products_chosen",
        content_name: products,
      });
    }
  }

  
  public trackLeadPopupOpened(step: string) {
    if (window.dataLayer) {
        window.dataLayer.push({
          event: "popup",
          content_name: "opened",
          content_type: "enrollment_abandoned",
          content_id: step,
        });
    }
  }

  public trackLeadPopupClosed() {
    if (window.dataLayer) {
        window.dataLayer.push({
          event: "popup",
          content_name: "closed",
          content_type: "enrollment_abandoned",
        });
    }
  }

   public trackPopupLead( step: string) {
    if (window.dataLayer) {
        window.dataLayer.push({
          event: "generate_lead",
          leadtype: "callback",
          leadkampagne: "enrollment_abandoned",
          content_id: step,
        });
    }
  }

  public trackLeadFromPopupComponent(campaign: string) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "generate_lead",
        leadkampagne: campaign,
        leadtype: "callback",
      });
    }
  }

  public trackLeadFromProductCard(flow: TrackingFlowTypeEnum) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "generate_lead",
        leadkampagne: "ring_mig_op+snak_med_en_raadgiver",
        leadtype: "callback",
        flow,
      });
    }
  }

  public trackPersonaTest= (step: any, values: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_id: step,
        content_type: "personalityTest_asbjoern",
        content_name: values,
      });
    }
  }

  public trackPersonaTestCta = (step: any, persona:string, url: string, linkText: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_id: step,
        content_type: "personalityTest_asbjoern",
        content_name: persona,
        link_url: url,
        link_text: linkText,
      });
    }
  }

  public trackBalanceTest = (step: any, values: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_id: step,
        content_type: "balanceTest",
        content_name: values,
      });
    }
  }

  public trackBalanceTestCta = (step: any, result:string, url: string, linkText: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_id: step,
        content_type: "balanceTest",
        content_name: result,
        link_url: url,
        link_text: linkText,
      });
    }
  }

  public trackTestSharer = (contentType: string, url: string, linkText: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_id: "sharer",
        content_type: contentType,
        link_url: url,
        link_text: linkText,
      });
    }
  }

  public trackSalaryCheck = (contentName: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_type: "salary_check",
        content_name: contentName,
      });
    }
  }

  public trackSalaryCheckWithValue = (contentName: string, value: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "calculator",
        content_type: "salary_check",
        content_name: contentName,
        numeric_value: value,
      });
    } 
  }

  public trackBehaviourProfiling = (contentid: string, contentType: string, contentName: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "profiling",
        content_id: contentid,
        content_name: contentName,
        content_type: contentType,
      });
    }
  }
  public trackBehaviourProfilingCta = (result:string, url: string, linkText: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "profiling",
        content_id: "behaviour_cta",
        content_type: "behavioral",
        content_name: result,
        link_url: url,
        link_text: linkText,
      });
    }
  }
  public trackBehaviourProfilingLead = () => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "generate_lead",
        leadkampagne: "ring_mig_op+adfærdsanalyse",
        leadtype: "callback",
      });
    }
  }

  public trackBehaviourProfilingLoader = () => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "profiling",
        content_id: "behavioural_loading",
        content_type: "behavioral",
        content_name: "start_loading",
      });
    }
  }
}
export default Analytics;
